/**
 * React Query hooks for service runs, recurrence rules and assignments.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import {
  getServiceRuns, getServiceProductRuns, updateServiceRun, propagateRunAssignments, createRecurrenceRule, generateRuns, createAssignment, deleteAssignment,
} from '../services/serviceRunService';
import type { RecurrenceRuleFormData, RunAssignmentFormData, RunEditFormData, RunEditMode } from '../types';

const RUNS = 'service-runs';

export function useServiceRuns(dateFrom: string, dateTo: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [RUNS, dateFrom, dateTo],
    queryFn: () => getServiceRuns(dateFrom, dateTo),
    enabled: options?.enabled ?? true,
  });
}

/** Upcoming scheduled runs of one product — the add/reschedule run picker. */
export function useServiceProductRuns(productId: string | undefined, dateFrom: string, dateTo: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [RUNS, 'product', productId, dateFrom, dateTo],
    queryFn: () => getServiceProductRuns(productId as string, dateFrom, dateTo),
    enabled: (options?.enabled ?? true) && !!productId,
  });
}

/** Create a recurrence rule then generate its runs in one flow. */
export function useCreateSchedule() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ data, locationId }: { data: RecurrenceRuleFormData; locationId: string }) => {
      const rule = await createRecurrenceRule(data, locationId);
      const { generated } = await generateRuns(rule.id);
      return { rule, generated };
    },
    onSuccess: ({ generated }) => {
      qc.invalidateQueries({ queryKey: [RUNS] });
      qc.invalidateQueries({ queryKey: ['service-products', 'stats'] });
      notify.success(t('services.runs.generated', '{{count}} runs generated', { count: generated }));
    },
    onError: () => notify.error(t('services.runs.scheduleError', 'Could not create the schedule')),
  });
}

/** Edit a run's per-instance fields, optionally propagating across its series. */
export function useUpdateServiceRun() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data, editMode }: { id: string; data: RunEditFormData; editMode?: RunEditMode }) =>
      updateServiceRun(id, data, { editMode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RUNS] });
      notify.success(t('services.runs.updated', 'Run updated'));
    },
    onError: () => notify.error(t('services.runs.updateError', 'Could not update the run')),
  });
}

/** Propagate a run's crew onto its series (this_and_future | all). */
export function usePropagateRunAssignments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, editMode }: { id: string; editMode: 'this_and_future' | 'all' }) =>
      propagateRunAssignments(id, editMode),
    onSuccess: () => qc.invalidateQueries({ queryKey: [RUNS] }),
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: RunAssignmentFormData) => createAssignment(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RUNS] });
      notify.success(t('services.runs.assigned', 'Assigned'));
    },
    onError: () => notify.error(t('services.runs.assignError', 'Could not assign')),
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAssignment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [RUNS] }),
  });
}
